import getTranslateFunc from 'utils/getTranslateFunc';

const normalizeLanguageCode = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .split(/[-_]/)[0];

const findLocale = (locales = [], code = '') => {
  const normalizedCode = normalizeLanguageCode(code);
  if (!normalizedCode) return null;

  return (Array.isArray(locales) ? locales : []).find(
    (locale) => normalizeLanguageCode(locale?.code) === normalizedCode
  ) || null;
};

/**
 * Add-on layer for the Mobile Placement / Custom Content instruction language.
 *
 * The existing mobile runtime keeps the Login/UI language as the Solution locale.
 * This add-on only separates the authored Activity content locale when registration
 * has an explicit instructionLanguage. If there is no explicit instruction language,
 * the existing runtime values are returned unchanged.
 */
export const applyMobileInstructionLanguageAddon = ({
  enabled = false,
  instructionLanguage,
  interfaceLocaleCode,
  activityLocaleCode,
  languageMode,
} = {}) => {
  const requestedInstructionLanguage = normalizeLanguageCode(instructionLanguage);

  if (!enabled || !requestedInstructionLanguage) {
    return {
      interfaceLocaleCode,
      activityLocaleCode,
      languageMode,
      requestedInstructionLanguage: '',
      applied: false,
    };
  }

  return {
    interfaceLocaleCode,
    activityLocaleCode: requestedInstructionLanguage,
    // Mobile Registration currently chooses an instruction language, not a
    // transition mode. Keep the existing mode when present; otherwise immersion.
    languageMode: languageMode || 'immersion',
    requestedInstructionLanguage,
    applied: normalizeLanguageCode(activityLocaleCode) !== requestedInstructionLanguage,
  };
};

/**
 * Presentation-only selector for Placement menus.
 * It does not mutate Redux state and does not alter the normal application UI locale.
 * The assessment attempt is authoritative; learner onboarding is the safe fallback.
 */
export const selectMobilePlacementLanguageAddon = (state = {}, assessmentAttempt = null) => {
  const common = state.common || {};
  const flinkPlay = state.flinkPlay || {};
  const learner = flinkPlay.learner || {};
  const fallbackTranslate = flinkPlay.solutionTranslate || state.status?.translate || null;
  const fallbackLocale = flinkPlay.activityLocale || flinkPlay.solutionLocale || state.status?.lang || null;

  const requestedInstructionLanguage = normalizeLanguageCode(
    assessmentAttempt?.instructionLanguage
    || assessmentAttempt?.language
    || learner?.customContentOnboarding?.instructionLanguage
  );

  if (!requestedInstructionLanguage) {
    return {
      active: false,
      localeCode: normalizeLanguageCode(fallbackLocale?.code) || 'en',
      locale: fallbackLocale,
      translate: fallbackTranslate,
    };
  }

  const locale = findLocale(common.locales, requestedInstructionLanguage)
    || (normalizeLanguageCode(fallbackLocale?.code) === requestedInstructionLanguage
      ? fallbackLocale
      : { code: requestedInstructionLanguage, name: requestedInstructionLanguage, displayName: requestedInstructionLanguage });

  const translate = Array.isArray(common.strings) && common.strings.length
    ? getTranslateFunc(common.strings, requestedInstructionLanguage)
    : fallbackTranslate;

  return {
    active: true,
    localeCode: requestedInstructionLanguage,
    locale,
    translate: translate || fallbackTranslate,
  };
};

export const preferPlacementLocalizedActivityLabel = ({
  item = {},
  menuState = {},
  active = false,
} = {}) => {
  if (active) {
    return item?.formattedName
      || item?.activityName
      || menuState?.sourceActivityName
      || menuState?.label
      || '';
  }

  return menuState?.sourceActivityName
    || item?.activityName
    || item?.formattedName
    || menuState?.label
    || '';
};

export const appendPlacementLocaleDiagnostic = (event, payload = {}) => {
  if (typeof window === 'undefined') return;

  const storage = window.Capacitor?.Plugins?.FLCOfflineStorage;
  if (!storage || typeof storage.appendDebugLog !== 'function') return;

  let serializedPayload = '{}';
  try {
    serializedPayload = JSON.stringify(payload || {});
  } catch (error) {
    serializedPayload = JSON.stringify({ serializationError: String(error?.message || error || 'unknown') });
  }

  Promise.resolve(storage.appendDebugLog({
    text: `[PLACEMENT_LOCALE] ${event} ${serializedPayload}`,
  })).catch(() => {});
};

export { normalizeLanguageCode as normalizePlacementLanguageCode };
